// packages/core/src/learning/school-modules/value-investing.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'value-investing' school. LearningModule, surface 'school'.
//   module id   = 'school-value-investing'  (matches exhibit nativeModuleId)
//   homeSchool  = 'value-investing'
// PROMOTED: five authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_VALUE_INVESTING_MODULE: LearningModule = {
	id: 'school-value-investing',
	title: 'Value Investing',
	description:
		'The lens that separates price from value — margin of safety, spotting value traps, patience versus stubbornness, and conviction kept honest by humility.',
	emoji: '🔍',
	accentColor: '#1f5f9e',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'value-investing',
	lessons: [
		{
			id: 'school-value-investing-01',
			title: 'Price is not value',
			description:
				'This lesson teaches the central value investing belief that what something costs and what it is worth can separate.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['value-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['value-investing', 'price-value-gap', 'intrinsic-worth'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['price value gap', 'market mood', 'intrinsic worth'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Value investing starts with a problem you see everywhere: people treat the price like it tells the whole truth. This school says the price is only what the market is offering today.',
				},
				{
					type: 'paragraph',
					text: 'The value lens believes an asset can trade below, above, or near what it may actually be worth. The market is not always calm. It can be scared, excited, impatient, distracted, or wrong.',
				},
				{
					type: 'bullet_list',
					items: [
						'Price is the offer',
						'Value is the estimate',
						'Mood can move both',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is assuming a falling price automatically means opportunity.',
				},
				{
					type: 'example',
					text: 'A company drops from $80 to $40. That does not make it cheap by itself. If the business is worth $25, it is still expensive. If the business is worth $70 and the problem is temporary, the value investor gets interested.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that value is never perfectly knowable. It is an estimate built from facts, assumptions, judgment, and humility.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to slowing down before reacting. The action is not to obey the market or fight it. The action is to ask what the price is assuming.',
				},
				{
					type: 'key_takeaway',
					text: 'Value investing starts when you stop treating price as truth.',
				},
			],
			audioScript:
				'Value investing begins with a simple but powerful separation: price is not value. Price is what someone is willing to pay today. Value is an estimate of what something may actually be worth based on its assets, earnings power, durability, risk, and future cash flows.\n\nThat difference sounds obvious, but in real life people forget it constantly. When something goes up, people assume it must be better. When something goes down, people assume it must be worse, or they assume it must be a bargain. Value investing says both reactions can be lazy. The market price is information, but it is not a final answer.\n\nThink about how people behave with normal purchases. If a jacket goes from $200 to $80, that feels like a deal. But what if the jacket is poorly made, does not fit, and you would never use it? The lower price did not create value for you. It only created the appearance of a bargain. Now apply that same idea to investing. A stock that falls by half is not automatically cheap. A stock that rises for years is not automatically expensive. The question is always, “What is this actually worth compared with what it costs?”\n\nThe value investing lens is paying attention to the gap. Sometimes the market becomes too pessimistic. A business may face a temporary problem, a bad news cycle, or a period where investors simply do not care about that industry. If the underlying value is stronger than the price suggests, a value investor sees possible opportunity.\n\nBut the lens has a danger. Estimating value is hard. You can be wrong about the business. You can be wrong about management. You can be wrong about competition. You can be wrong about how long the problem lasts. A company can look cheap because the market is correctly seeing something ugly that you missed.\n\nThis is why value investing is not just bargain hunting. It is disciplined judgment. The mature version does not say, “This price is down, so it is good.” It says, “The price is down. What does that imply? What would the business need to be worth for this to make sense? What could make my estimate wrong?”\n\nInside Nisk, this matters because the same habit applies outside investing too. People overpay for status, panic when numbers move, and confuse visible price with hidden value. A value lens teaches you to slow the decision down. Ask what is being priced in. Ask what is being ignored. Ask whether you are seeing value or just reacting to movement.\n\nThe takeaway is direct. Price is the market’s current offer. Value is your best estimate. The skill is not worshiping either one.',
			flashcards: [
				{
					id: 'school-value-investing-01-fc-1',
					role: 'main_idea',
					front: 'What does value investing separate?',
					back: 'It separates current price from estimated value.',
				},
				{
					id: 'school-value-investing-01-fc-2',
					role: 'watch_out',
					front: 'What is a false bargain?',
					back: 'Something that looks cheap but is worth even less.',
				},
				{
					id: 'school-value-investing-01-fc-3',
					role: 'use_it',
					front: 'What should price make you ask?',
					back: 'Ask what the market price is assuming.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Compare price and value',
				prompt: 'Write one example where a lower price did not actually mean better value.',
			},
			revision: 1,
		},
		{
			id: 'school-value-investing-02',
			title: 'Margin of safety protects humility',
			description:
				'This lesson teaches why value investors want room for error instead of perfect forecasts.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['value-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['value-investing', 'margin-of-safety', 'downside-first'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['margin of safety', 'room for error', 'downside first'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Value investing assumes you will be wrong sometimes. The question is whether your plan leaves room for that.',
				},
				{
					type: 'paragraph',
					text: 'The margin of safety idea says the price should give you some protection if your estimate is imperfect. It is not a magic shield. It is a humility tool.',
				},
				{
					type: 'bullet_list',
					items: [
						'Estimates can be wrong',
						'Bad news can arrive',
						'Price should leave room',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is building a thesis that only works if everything goes right.',
				},
				{
					type: 'example',
					text: 'If you think a business is worth $100, paying $98 leaves little room for mistakes. Paying much less may create more room, but only if your value estimate is reasonable.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is false comfort. A margin of safety can be imaginary if the business is weaker than you think.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your own life too. Runway, savings, and lower pressure are personal margins of safety.',
				},
				{
					type: 'key_takeaway',
					text: 'Margin of safety is not fear. It is respect for being human.',
				},
			],
			audioScript:
				'Margin of safety is one of the most important ideas in value investing because it begins with humility. It says, “Your estimate might be wrong. The future might be messier than your model. Leave room.”\n\nThat is a very different attitude from pretending you can forecast everything perfectly. A value investor may study a business carefully, read the numbers, understand the industry, and still admit that things can go wrong. Revenue can weaken. Costs can rise. Management can make poor decisions. Competition can get stronger. The economy can shift. A legal issue, product problem, or reputation problem can change the picture.\n\nMargin of safety is the attempt to protect yourself from that uncertainty. It means you do not want to pay a price that only makes sense if the best version of the future happens. You want some gap between what you think something is worth and what you are being asked to pay.\n\nA simple example helps. Say someone believes a business is worth about $100 per share based on their analysis. If they pay $98, there is almost no room for error. If the estimate is slightly too high, or if the business weakens, the whole thesis can break. If the person pays much less, there may be more room for mistakes. But this only matters if the value estimate itself is grounded. A random guess does not become wise because you call it a margin of safety.\n\nThat is the key. Margin of safety is not a slogan. It is not a guarantee. It is not a way to make risky decisions feel safe. It is a discipline that forces you to ask, “What if I am wrong?”\n\nThis lesson also applies beyond investing. In your own money life, emergency savings are a margin of safety. Lower fixed expenses can be a margin of safety. A skill you can use in another job can be a margin of safety. Having time before a deadline can be a margin of safety. The idea is the same: do not build a life that only works if everything goes perfectly.\n\nThe value investing lens protects against fragile thinking. It says that risk is not only about dramatic disaster. Risk is also about having no room for normal mistakes. If your plan only survives in the cleanest possible version of reality, it is probably weaker than it looks.\n\nThe blind spot is that people can fake this idea. They can say, “I have a margin of safety,” while using bad assumptions. They can ignore a deteriorating business because the stock looks cheap. They can keep lowering their estimate slowly while still believing they are protected. A margin of safety that exists only in your imagination is not protection.\n\nInside Nisk, treat this as a mindset. Before you get excited about upside, ask what protects the downside. Before you trust a number, ask how wrong it could be. Before you build a plan, ask whether it can survive a normal bad month, a late payment, a job change, or a market drop.\n\nThe takeaway is simple. Margin of safety is respect for uncertainty. It does not remove risk, but it makes your thinking less fragile.',
			flashcards: [
				{
					id: 'school-value-investing-02-fc-1',
					role: 'main_idea',
					front: 'What is margin of safety?',
					back: 'Room for error between price and estimated value.',
				},
				{
					id: 'school-value-investing-02-fc-2',
					role: 'watch_out',
					front: 'When is safety fake?',
					back: 'When the value estimate is weak or the business is breaking.',
				},
				{
					id: 'school-value-investing-02-fc-3',
					role: 'use_it',
					front: 'How does this apply personally?',
					back: 'Build room for error through runway, lower pressure, and honest assumptions.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
			revision: 1,
		},
		{
			id: 'school-value-investing-03',
			title: 'Cheap can be a trap',
			description:
				'This lesson teaches why value investing must separate bargains from broken businesses.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['value-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['value-investing', 'value-trap', 'thesis-discipline'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['value trap', 'business quality', 'thesis discipline'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Value investing can go wrong when cheap becomes the whole argument. A low price can hide a real problem.',
				},
				{
					type: 'paragraph',
					text: 'This school looks for mispriced value, but it has to ask why the market is skeptical. Sometimes the crowd is wrong. Sometimes the crowd is seeing decay.',
				},
				{
					type: 'bullet_list',
					items: [
						'Sales may be shrinking',
						'Debt may be heavy',
						'The business may be losing relevance',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is saying “it cannot go much lower” without understanding what is broken.',
				},
				{
					type: 'example',
					text: 'A company looks cheap compared with past earnings. But if customers are leaving, margins are falling, and competitors are winning, the past may not tell you much about future value.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is emotional. A value investor must be independent without becoming stubborn.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to writing what would prove you wrong. A real thesis needs an exit from self deception.',
				},
				{
					type: 'key_takeaway',
					text: 'A bargain is cheap compared with value. A trap is cheap compared with the past.',
				},
			],
			audioScript:
				'Value investing has a dangerous failure mode: the value trap. A value trap is something that looks cheap but is cheap for a reason. The numbers may look attractive at first glance, but the business is weaker than the investor understands.\n\nThis matters because value investing trains you to look where other people are not excited. That can be useful. The market can overreact. Investors can ignore boring businesses. Bad news can create fear that pushes price below value. But if you only focus on the low price, you can miss the reason the price is low.\n\nA company may look cheap compared with last year’s earnings. But what if last year was the peak? What if customers are leaving? What if a better competitor is taking share? What if the company has too much debt? What if the product is becoming less relevant? What if management is using accounting to make things look healthier than they are?\n\nIn that case, the old numbers become a trap. You are not buying a bargain. You are buying a business whose future may be worse than its past.\n\nThe value lens has to ask a hard question: “Why is this cheap?” Not in a defensive way. Not as an excuse to avoid every unpopular idea. But as a real investigation. If the answer is temporary fear, misunderstanding, neglect, or a solvable problem, the idea may still deserve attention. If the answer is permanent damage, shrinking demand, poor economics, or bad incentives, cheapness may be warning you.\n\nImagine a store chain that used to earn strong profits. The stock falls, and the valuation looks low compared with old earnings. A surface level value investor might say, “This is cheap.” A better value investor asks, “Are those earnings coming back?” If online competitors have changed customer behavior, if the stores need expensive upgrades, and if debt is high, the low price may not be a gift. It may be the market adjusting to a weaker future.\n\nThe emotional challenge is big. Value investing requires independence. You have to be willing to disagree with the crowd. But independence can become stubbornness. You start thinking every negative sign is just market fear. You keep defending the thesis because admitting the business is broken feels like losing your identity.\n\nThat is why thesis discipline matters. Before getting attached, write what would prove you wrong. Maybe sales keep falling. Maybe margins fail to recover. Maybe debt becomes harder to manage. Maybe the industry changes faster than expected. If those things happen, the thesis is not being misunderstood. It may be failing.\n\nInside Nisk, this lesson is about judgment. You are not being told what to buy or avoid. You are learning how a worldview protects itself from its own blind spot. Value investing is powerful when it finds a real gap between price and value. It is dangerous when it confuses a low price with proof.\n\nThe takeaway is clean. A bargain is cheap compared with value. A trap is cheap compared with memory.',
			flashcards: [
				{
					id: 'school-value-investing-03-fc-1',
					role: 'main_idea',
					front: 'What is a value trap?',
					back: 'Something that looks cheap because the business is weaker than it appears.',
				},
				{
					id: 'school-value-investing-03-fc-2',
					role: 'watch_out',
					front: 'Why can past earnings mislead?',
					back: 'The future may be worse if the business is losing relevance.',
				},
				{
					id: 'school-value-investing-03-fc-3',
					role: 'use_it',
					front: 'What question protects you?',
					back: 'Ask why it is cheap and what would prove the thesis wrong.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Name the trap',
				prompt: 'Write three reasons a cheap looking opportunity might actually be broken.',
			},
			revision: 1,
		},
		{
			id: 'school-value-investing-04',
			title: 'Patience is not stubbornness',
			description:
				'This lesson teaches how value investing depends on temperament, waiting, and knowing when the thesis has changed.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['value-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: ['value-investing', 'patient-temperament', 'thesis-change'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'patient temperament',
				'thesis change',
				'emotional discipline',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Value investing often asks you to wait while the crowd disagrees with you. That can feel smart. It can also become self deception.',
				},
				{
					type: 'paragraph',
					text: 'This school believes patience matters because price and value may take time to reconnect. But patience only helps when the original thesis remains intact.',
				},
				{
					type: 'bullet_list',
					items: [
						'Waiting can be rational',
						'Pride can look like patience',
						'Evidence must update the thesis',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating every loss as proof that you are early.',
				},
				{
					type: 'example',
					text: 'If a business is stable and the market is temporarily fearful, patience may help. If the business keeps weakening, waiting may just be refusing to learn.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that value investing gives slow feedback. You may not know quickly whether you are disciplined or wrong.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to writing rules before emotion takes over. Define what would make you wait, and what would make you rethink.',
				},
				{
					type: 'key_takeaway',
					text: 'Patience is valuable only when it is attached to evidence.',
				},
			],
			audioScript:
				'Value investing has a reputation for patience. That reputation is earned, but it is also dangerous. Patience can be wisdom. Patience can also become stubbornness wearing better clothes.\n\nThe value investing lens says price and value may take time to reconnect. The market may ignore a business for months or years. People may be chasing flashier stories. A solid but unpopular company may sit there while nothing exciting happens. In those moments, patience matters. If your analysis is sound and the business is holding up, the ability to wait can be part of the edge.\n\nBut here is the problem. Being early and being wrong can feel almost identical for a while. In both cases, the price may move against you. In both cases, other people may disagree. In both cases, you may feel pressure to defend your thinking. The difference is evidence.\n\nA patient value investor keeps checking whether the thesis is still alive. Are the business facts developing roughly as expected? Is the balance sheet still manageable? Are customers still there? Is management acting rationally? Is the industry still healthy enough for the original estimate to make sense?\n\nA stubborn investor does something different. They ignore new information because it hurts. They call every problem temporary. They call every critic emotional. They keep repeating the original thesis even when the facts have changed.\n\nImagine a business that looks undervalued because investors are scared about a short term issue. If the issue passes, profits stabilize, and the business remains strong, patience may be rational. Now imagine a different business. Sales keep declining. Debt becomes more stressful. Competitors keep taking customers. Management keeps making excuses. In that case, waiting may not be discipline. It may be denial.\n\nThis is one of the hardest parts of value investing. The feedback loop is slow. A trader may know quickly when a setup fails. A value investor may need more time. But more time does not mean infinite patience. It means having a clear reason for waiting and a clear reason for rethinking.\n\nInside Nisk, this connects to your real money life too. People do this with careers, relationships, business ideas, and spending habits. They tell themselves they are being patient when they are actually avoiding a hard update. Or they quit too early because discomfort feels like proof of failure. The value lens teaches a middle path: wait when the facts support waiting, update when the facts change.\n\nA useful habit is to write the thesis before the emotions arrive. What do you believe? What would make the idea stronger? What would weaken it? What would prove that the situation has changed? This gives future you something to compare against when the market gets loud.\n\nThe takeaway is simple. Patience is not closing your eyes. Patience is staying calm while you keep checking the evidence.',
			flashcards: [
				{
					id: 'school-value-investing-04-fc-1',
					role: 'main_idea',
					front: 'Why does value need patience?',
					back: 'Price and value may take time to reconnect.',
				},
				{
					id: 'school-value-investing-04-fc-2',
					role: 'watch_out',
					front: 'When does patience become stubbornness?',
					back: 'When you ignore evidence that the thesis has changed.',
				},
				{
					id: 'school-value-investing-04-fc-3',
					role: 'use_it',
					front: 'How do you stay honest?',
					back: 'Write what would make you wait and what would make you rethink.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Write your rethink rule',
				prompt: 'Write one sign that would make you stay patient and one sign that would make you change your mind.',
			},
			revision: 1,
		},
		{
			id: 'school-value-investing-05',
			title: 'Value investing breaks without humility',
			description:
				'This lesson teaches where the value lens can fail through overconfidence, weak analysis, and false certainty.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['value-investing'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 4,
			tags: [
				'value-investing',
				'circle-of-competence',
				'false-precision',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'circle of competence',
				'analysis humility',
				'false precision',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Value investing can make people feel smarter than the market. That is exactly where it gets dangerous.',
				},
				{
					type: 'paragraph',
					text: 'This school is strongest when it combines independent thinking with humility. You need the confidence to disagree and the honesty to admit what you do not understand.',
				},
				{
					type: 'bullet_list',
					items: [
						'Models can be wrong',
						'Knowledge has limits',
						'The market may know something',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is building a precise estimate from shaky assumptions.',
				},
				{
					type: 'example',
					text: 'You estimate a business is worth $100, but your estimate depends on growth, margins, and risk assumptions you barely understand. The number looks clean, but the foundation is weak.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is false precision. A spreadsheet can make uncertainty look more controlled than it really is.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens should end with a clear boundary. Know what you understand, know what you are guessing, and know where this worldview stops helping.',
				},
				{
					type: 'key_takeaway',
					text: 'Value investing works best when conviction and humility stay in the same room.',
				},
			],
			audioScript:
				'Value investing can create a very specific kind of overconfidence. You study the numbers. You estimate value. You decide the market is wrong. That can feel powerful. It can also make you believe you understand more than you actually do.\n\nThe strongest version of value investing is not arrogant. It is independent, but humble. It says the market can be wrong, but so can you. It says price and value can separate, but estimating value is difficult. It says patience matters, but patience without evidence can become denial.\n\nThis is why the idea of a circle of competence matters. A circle of competence is the area where you have enough understanding to make a thoughtful judgment. It is not about pretending you are stupid. It is about being honest about what you truly understand and what you are only repeating from other people.\n\nA business can look simple from the outside and still be complex underneath. You may understand the product but not the industry. You may understand the brand but not the balance sheet. You may understand the customer but not the competitive threat. You may understand the past but not the future. Value investing breaks when people turn partial understanding into full confidence.\n\nFalse precision is another danger. A spreadsheet can make a guess look official. You can type in growth rates, margins, discount rates, and future cash flows. The model gives you a clean number, maybe $100 per share. That number can feel solid because it looks mathematical. But if the assumptions are weak, the number is not solid. It is just a precise expression of uncertainty.\n\nImagine estimating a business is worth $100, but that estimate depends on margins improving, growth staying strong, competition staying weak, and management making smart decisions for years. If you do not understand those drivers well, the estimate may be much shakier than it looks. The spreadsheet did not remove uncertainty. It organized it.\n\nThe value lens also has to respect that the market may know something. The crowd is not always wise, but it is not always foolish either. Sometimes a low price reflects real risk. Sometimes the scary headlines are not overreaction. Sometimes the simple looking bargain has hidden problems.\n\nInside Nisk, this lesson matters because the same mistake shows up in personal finance. People make confident plans from weak assumptions. They assume income will rise, expenses will stay calm, health will cooperate, markets will behave, and life will fit the model. Then reality shows up. Humility means planning with room for error.\n\nThe best version of value investing holds two ideas together. First, the market can misprice things. Second, you can misread things. That balance is the whole game. Too much fear and you never act. Too much confidence and you fool yourself.\n\nThe takeaway is direct. Value investing works best when conviction and humility stay in the same room. You need enough confidence to think independently, and enough humility to know when your estimate is built on sand.',
			flashcards: [
				{
					id: 'school-value-investing-05-fc-1',
					role: 'main_idea',
					front: 'Why does value need humility?',
					back: 'Because estimating value depends on assumptions that can be wrong.',
				},
				{
					id: 'school-value-investing-05-fc-2',
					role: 'watch_out',
					front: 'What is false precision?',
					back: 'A clean number built from weak or uncertain assumptions.',
				},
				{
					id: 'school-value-investing-05-fc-3',
					role: 'use_it',
					front: 'What is the boundary check?',
					back: 'Ask what you understand, what you are guessing, and what could prove you wrong.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Draw your competence line',
				prompt: 'Write what you truly understand about an idea, then write what you are only guessing.',
			},
			revision: 1,
		},
	],
};
